import { useState, useCallback } from 'react';
import { message } from 'antd';
import { WirePaymentBuilder } from 'app/services/wirePaymentBuilder';

export enum WIRE_PAYMENT_STATUSES {
  PROCESSED = 'processed',
  REJECTED = 'rejected',
}

export enum WIRE_PAYMENT_PROCESSING_STATUSES {
  PROCESSING = 'PROCESSING',
  MULTIPLE_LOOPS_FOUND = 'MULTIPLE_LOOPS_FOUND',
  LOOP_NOT_FOUND = 'LOOP_NOT_FOUND',
  DOCUMENTS_MISSING = 'DOCUMENTS_MISSING',
  // PRISM_NOT_FOUND = 'PRISM_NOT_FOUND',
  // PRISM_FOUND = 'PRISM_FOUND',
  // VALIDATION_FAILED = 'VALIDATION_FAILED',
  MAX_APPROVED = 'MAX_APPROVED',
}

export enum WIRE_PAYMENT_EXTERNAL_STATUSES {
  PRISM = 'PRISM',
}

export enum WIRE_PAYMENT_PRISM_STATUSES {
  PRISM_NOT_FOUND = 'PRISM_NOT_FOUND',
  PRISM_FOUND_NOT_UPDATED = 'PRISM_FOUND_NOT_UPDATED',
  PRISM_UPDATED = 'PRISM_UPDATED',
  POWERSTREAM = 'POWERSTREAM',
  PROCESSING = 'PROCESSING',
}

export enum WIRE_PAYMENT_PRISM_STATUSES_TEXT {
  PRISM_NOT_FOUND = 'Prism Not Found',
  PRISM_FOUND_NOT_UPDATED = 'Prism Found Not Updated',
  PRISM_UPDATED = 'Prism Updated',
  POWERSTREAM = 'Powerstream',
  PROCESSING = 'Processing',
}

export enum WIRE_PAYMENT_PROCESSING_STATUSES_TEXT {
  PROCESSING = 'Under Review',
  MULTIPLE_LOOPS_FOUND = 'Multiple Loops Found',
  LOOP_NOT_FOUND = 'Loop Not Found',
  DOCUMENTS_MISSING = 'Documents Missing',
  PRISM_NOT_FOUND = 'Prism Not Found',
  PRISM_FOUND = 'Prism Found',
  VALIDATION_FAILED = 'Validation Failed',
  MAX_APPROVED = 'Max Approved',
}

export interface WirePayment {
  _id: string;
  transactionId?: string;
  externalTransactionId: string;
  depositId?: string;
  amount: number;
  propertyAddress: string;
  customer: {
    name?: string;
    email?: string;
  };
  status: WIRE_PAYMENT_STATUSES;
  processingStatus: {
    status: WIRE_PAYMENT_PROCESSING_STATUSES;
    missingDocs: string[];
    externalStatuses: { PRISM: string };
  };
  depositedDate: string;
  rejectedDate?: string;
  source: string;
  createdAt: string;
  updatedAt: string;
  primaryAgent: {
    name: string;
  };
  transaction: {
    agents: {
      name: string;
    }[];
    externalSourceID: string;
  };
}

interface UseWirePaymentsProps {
  initialPage?: number;
  initialPageSize?: number;
}

export const useWirePayments = ({
  initialPage = 1,
  initialPageSize = 10,
}: UseWirePaymentsProps = {}) => {
  const [loading, setLoading] = useState(false);
  const [wirePayments, setWirePayments] = useState<WirePayment[]>([]);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [total, setTotal] = useState(0);

  const fetchWirePayments = useCallback(
    async (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: WIRE_PAYMENT_STATUSES[];
      processingStatus?: WIRE_PAYMENT_PROCESSING_STATUSES[];
      createdAtRange?: [string, string];
      processedDateRange?: [string, string];
      depositedDateRange?: [string, string];
      rejectedDateRange?: [string, string];
      prismStatus?: string[];
      contractCare?: boolean;
    }) => {
      setLoading(true);
      try {
        const newPageSize = params?.pageSize || pageSize;
        const builder = new WirePaymentBuilder.builder()
          .setPage(params?.page || currentPage)
          .setLimit(newPageSize);

        if (params?.search) {
          builder.setSearch(params.search);
        }

        if (params?.status?.length) {
          builder.setStatus(params.status);
        }

        if (params?.processingStatus?.length) {
          builder.setProcessingStatus(params.processingStatus);
        }

        if (params?.createdAtRange) {
          builder.setCreatedAtGte(params.createdAtRange[0]);
          builder.setCreatedAtLte(params.createdAtRange[1]);
        }

        if (params?.processedDateRange) {
          builder.setProcessedDateGte(params.processedDateRange[0]);
          builder.setProcessedDateLte(params.processedDateRange[1]);
        }

        if (params?.depositedDateRange) {
          builder.setDepositedDateGte(params.depositedDateRange[0]);
          builder.setDepositedDateLte(params.depositedDateRange[1]);
        }

        if (params?.rejectedDateRange) {
          builder.setRejectedDateGte(params.rejectedDateRange[0]);
          builder.setRejectedDateLte(params.rejectedDateRange[1]);
        }

        if (params?.prismStatus?.length) {
          builder.setPrismStatus(params.prismStatus);
        }

        if (typeof params?.contractCare === 'boolean') {
          builder.setContractCare(params.contractCare);
        }

        const response = await builder.build().fetchNext();

        setWirePayments(response.data);
        setTotal(response.total);
        setCurrentPage(params?.page || currentPage);
        if (params?.pageSize) {
          setPageSize(params.pageSize);
        }
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || 'Failed to fetch wire payments'
        );
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  return {
    wirePayments,
    loading,
    currentPage,
    pageSize,
    setPageSize,
    total,
    fetchWirePayments,
  };
};
